import { useEffect, useState } from 'react';
import { useQuery } from 'urql';
import { queryDomainProviders } from '../../graphql/providers';
import type {
    DomainProvidersResponse,
    PageInfo,
    Provider,
    ProviderEdge,
    QueryVariables,
    UseProviderDataProps,
    UseProviderDataReturn
} from '../../provider/types';

export const useProviderData = ({
  selectedServices,
  selectedLocation,
  activeService,
  setServiceProviderMap,
  serviceProviderMap,
  ITEMS_PER_PAGE = 6
}: UseProviderDataProps): UseProviderDataReturn => {
  const [providers, setProviders] = useState<ProviderEdge[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);

  // Initialize query variables with filter
  const [queryVariables, setQueryVariables] = useState<QueryVariables>({
    paging: {
      first: ITEMS_PER_PAGE,
      after: null,
    },
    filter: {
      isDeleted: {
        is: false
      },
      services: {
        id: {
          in: [activeService]
        }
      },
      locations: {
        id: {
          eq: selectedLocation?.id
        }
      }
    }
  });

  const [result] = useQuery<DomainProvidersResponse>({
    query: queryDomainProviders,
    variables: queryVariables,
    pause: false,
    requestPolicy: 'network-only'
  });

  const { data, fetching, error } = result;

  // Clean up serviceProviderMap when services change
  useEffect(() => {
    if (selectedServices) {
      const selectedServiceIds = selectedServices.map(service => service.id);

      // Create a new map with only currently selected services
      const updatedMap: typeof serviceProviderMap = {};

      // Keep mappings only for currently selected services
      selectedServiceIds.forEach(serviceId => {
        if (serviceProviderMap[serviceId]) {
          updatedMap[serviceId] = serviceProviderMap[serviceId];
        }
      });

      // Update the service provider map if it's different
      if (Object.keys(updatedMap).length !== Object.keys(serviceProviderMap).length) {
        setServiceProviderMap(updatedMap);
      }
    }
  }, [selectedServices, serviceProviderMap, setServiceProviderMap]);

  // Handle data updates
  useEffect(() => {
    if (data) {
      setProviders(data.domainProviders.edges);
      setPageInfo(data.domainProviders.pageInfo);
      setIsLoading(false);

      // Auto-assign if no providers
      if (data.domainProviders.edges.length === 0) {
        setServiceProviderMap(prev => ({
          ...prev,
          [activeService]: { autoAssigned: true }
        }));
      }
    }
  }, [data, activeService, setServiceProviderMap]);

  // Handle error states
  useEffect(() => {
    if (error) {
      setIsLoading(false);
    }
  }, [error]);

  // Handle loading states
  useEffect(() => {
    setIsLoading(fetching);
  }, [fetching]);

  // Update query variables when activeService or location changes
  useEffect(() => {
    setQueryVariables(prev => ({
      ...prev,
      filter: {
        ...prev.filter,
        services: {
          id: {
            in: [activeService]
          }
        },
        locations: {
          id: {
            eq: selectedLocation?.id
          }
        }
      }
    }));
  }, [activeService, selectedLocation]);

  const handlePageChange = (direction: 'next' | 'previous'): void => {
    if (!pageInfo) return;

    const newVariables: QueryVariables = {
      ...queryVariables,
      paging: {}
    };

    if (direction === 'next' && pageInfo.hasNextPage) {
      newVariables.paging = {
        first: ITEMS_PER_PAGE,
        after: pageInfo.endCursor
      };
    } else if (direction === 'previous' && pageInfo.hasPreviousPage) {
      newVariables.paging = {
        last: ITEMS_PER_PAGE,
        before: pageInfo.startCursor
      };
    } else {
      return;
    }

    setQueryVariables(newVariables);
  };

  const handleProviderSelect = (provider: Provider): void => {
    // Only update the map for currently selected services
    if (selectedServices.some(service => service.id === activeService)) {
      setServiceProviderMap(prev => ({
        ...prev,
        [activeService]: provider
      }));
    }
  };

  // Check if all currently selected services have providers assigned
  const canProceed = selectedServices &&
    selectedServices.every(service => serviceProviderMap[service.id]);

  return {
    providers,
    isLoading,
    pageInfo,
    error,
    handlePageChange,
    handleProviderSelect,
    canProceed
  };
};
